import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TrendingUp, TrendingDown, Minus, Search, Plus, Trash2, Play, RefreshCw, AlertCircle, CheckCircle2, Clock, Terminal } from 'lucide-react';
import { parseApiDate } from '@framework/utils/dateUtils';

const POLL_INTERVAL = 2500;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function PositionBadge({ position, totalChecked }) {
  if (!position) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500">
        –
        {totalChecked > 0 && (
          <span className="text-gray-300 dark:text-gray-600 font-normal">/{totalChecked}</span>
        )}
      </span>
    );
  }
  const color =
    position <= 10
      ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
      : position <= 30
      ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400'
      : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${color}`}>
      #{position}
    </span>
  );
}

function TrendIcon({ current, prev }) {
  if (!current || !prev) return <Minus className="w-4 h-4 text-gray-300 dark:text-gray-600" />;
  if (current < prev) return <TrendingUp className="w-4 h-4 text-green-500" title={`Up from #${prev}`} />;
  if (current > prev) return <TrendingDown className="w-4 h-4 text-red-400" title={`Down from #${prev}`} />;
  return <Minus className="w-4 h-4 text-gray-400" title="No change" />;
}

function timeAgo(isoString) {
  if (!isoString) return '—';
  const parsed = parseApiDate(isoString);
  if (!parsed) return '—';
  const diff = Math.floor((Date.now() - parsed.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const StoreRankingsPage = ({ api }) => {
  const [keywords, setKeywords] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanMessage, setScanMessage] = useState('');
  const [scanLog, setScanLog] = useState([]);
  const [scanJobId, setScanJobId] = useState(null);
  const [scanError, setScanError] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const pollRef = useRef(null);
  const logEndRef = useRef(null);
  const prevPartialCount = useRef(0);

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const loadData = useCallback(async () => {
    try {
      const [kws, rnks] = await Promise.all([api.getKeywords(), api.getRankings()]);
      setKeywords(kws);
      setRankings(rnks);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);

  // Scroll log to bottom when new lines appear
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [scanLog]);

  // ---------------------------------------------------------------------------
  // Scan polling
  // ---------------------------------------------------------------------------

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPolling = (jobId) => {
    stopPolling();
    prevPartialCount.current = 0;

    pollRef.current = setInterval(async () => {
      try {
        const data = await api.getScanStatus(jobId);
        setScanProgress(data.progress || 0);
        setScanMessage(data.message || '');

        // Append newly completed keywords to the live log
        const partial = data.result?.partial_results || data.result?.rankings || [];
        const newItems = partial.slice(prevPartialCount.current);
        if (newItems.length > 0) {
          setScanLog(prev => [
            ...prev,
            ...newItems.map(item => ({
              keyword: item.keyword,
              position: item.position,
              total_checked: item.total_checked,
              label: item.label || (item.position ? `#${item.position}` : 'not found'),
            })),
          ]);
          prevPartialCount.current = partial.length;
        }

        if (data.status === 'completed' || data.status === 'failed') {
          stopPolling();
          setScanning(false);
          if (data.status === 'failed') {
            setScanError(data.message || 'Scan failed');
          } else {
            // Refresh rankings table with fresh data
            await loadData();
          }
        }
      } catch (err) {
        stopPolling();
        setScanning(false);
        setScanError(err.message);
      }
    }, POLL_INTERVAL);
  };

  useEffect(() => () => stopPolling(), []);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const handleScan = async () => {
    setScanError(null);
    setScanLog([]);
    setScanProgress(0);
    setScanMessage('Starting scan...');
    setScanning(true);
    prevPartialCount.current = 0;

    try {
      const { job_id } = await api.startScan();
      setScanJobId(job_id);
      startPolling(job_id);
    } catch (err) {
      setScanning(false);
      setScanError(err.message);
    }
  };

  const handleAddKeyword = async (e) => {
    e.preventDefault();
    if (!newKeyword.trim()) return;
    setAddLoading(true);
    try {
      await api.addKeyword(newKeyword.trim().toLowerCase());
      setNewKeyword('');
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemoveKeyword = async (id) => {
    try {
      await api.removeKeyword(id);
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const scanButtonLabel = scanning
    ? `Scanning… ${scanProgress}%`
    : 'Run Scan';

  const activeKeywords = keywords.filter(k => k.active);

  // Merge ranking data into keyword list for the table
  const rankingMap = Object.fromEntries(rankings.map(r => [r.id, r]));

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <RefreshCw className="w-6 h-6 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Search className="w-6 h-6 text-orange-500" />
            Play Store Rankings
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            FooZenie · <code className="text-xs">com.ioriented.foozenie</code>
          </p>
        </div>

        <button
          onClick={handleScan}
          disabled={scanning || activeKeywords.length === 0}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm
                     bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed
                     text-white transition-colors shadow-sm"
        >
          {scanning
            ? <RefreshCw className="w-4 h-4 animate-spin" />
            : <Play className="w-4 h-4" />
          }
          {scanButtonLabel}
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Scan progress + live log */}
      {(scanning || scanLog.length > 0) && (
        <div className="bg-gray-900 dark:bg-gray-950 rounded-xl overflow-hidden shadow-inner">
          {/* Progress bar */}
          <div className="px-4 pt-3 pb-2 flex items-center gap-3">
            <Terminal className="w-4 h-4 text-orange-400 flex-shrink-0" />
            <div className="flex-1 bg-gray-700 rounded-full h-1.5">
              <div
                className="bg-orange-500 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${scanProgress}%` }}
              />
            </div>
            <span className="text-xs text-gray-400 tabular-nums w-8 text-right">{scanProgress}%</span>
          </div>

          {/* Log lines */}
          <div className="px-4 pb-4 font-mono text-xs space-y-1 max-h-48 overflow-y-auto">
            {scanMessage && (
              <div className="text-gray-400 flex items-center gap-2">
                {scanning && <RefreshCw className="w-3 h-3 animate-spin text-orange-400" />}
                {scanMessage}
              </div>
            )}
            {scanLog.map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />
                <span className="text-gray-300">{item.keyword}</span>
                <span className="text-gray-600">→</span>
                <span className={
                  !item.position
                    ? 'text-gray-500'
                    : item.position <= 10
                    ? 'text-green-400 font-bold'
                    : item.position <= 30
                    ? 'text-yellow-400 font-bold'
                    : 'text-red-400 font-bold'
                }>
                  {item.label}
                </span>
                {item.total_checked > 0 && !item.position && (
                  <span className="text-gray-600">(checked {item.total_checked})</span>
                )}
              </div>
            ))}
            {scanError && (
              <div className="flex items-center gap-2 text-red-400">
                <AlertCircle className="w-3 h-3" /> {scanError}
              </div>
            )}
            <div ref={logEndRef} />
          </div>
        </div>
      )}

      {/* Rankings table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="font-semibold text-gray-900 dark:text-white">
            Current Rankings
            <span className="ml-2 text-xs font-normal text-gray-400">
              {activeKeywords.length} keyword{activeKeywords.length !== 1 ? 's' : ''} tracked
            </span>
          </h2>
        </div>

        {activeKeywords.length === 0 ? (
          <div className="px-5 py-10 text-center text-gray-400 text-sm">
            No keywords yet — add some below.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-gray-700">
                  <th className="px-5 py-3">Keyword</th>
                  <th className="px-4 py-3 text-center">Rank</th>
                  <th className="px-4 py-3 text-center">Trend</th>
                  <th className="px-4 py-3 text-center hidden sm:table-cell">Prev</th>
                  <th className="px-4 py-3 hidden md:table-cell">Last Scan</th>
                  <th className="px-4 py-3 text-center hidden sm:table-cell">Checked</th>
                  <th className="px-4 py-3 w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                {activeKeywords.map(kw => {
                  const r = rankingMap[kw.id] || {};
                  return (
                    <tr
                      key={kw.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                    >
                      <td className="px-5 py-3 font-medium text-gray-800 dark:text-gray-200">
                        {kw.keyword}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <PositionBadge position={r.current_position} totalChecked={r.total_checked} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center">
                          <TrendIcon current={r.current_position} prev={r.prev_position} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center hidden sm:table-cell">
                        {r.prev_position ? (
                          <span className="text-xs text-gray-400">#{r.prev_position}</span>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {timeAgo(r.scanned_at)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center hidden sm:table-cell">
                        <span className="text-xs text-gray-400">
                          {r.total_checked ? r.total_checked : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleRemoveKeyword(kw.id)}
                          className="text-gray-300 hover:text-red-400 dark:text-gray-600 dark:hover:text-red-400 transition-colors"
                          title="Remove keyword"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add keyword */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
        <h2 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <Plus className="w-4 h-4 text-orange-500" />
          Add Keyword
        </h2>
        <form onSubmit={handleAddKeyword} className="flex gap-3">
          <input
            type="text"
            value={newKeyword}
            onChange={e => setNewKeyword(e.target.value)}
            placeholder="e.g. recipe keeper"
            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm
                       focus:outline-none focus:ring-2 focus:ring-orange-400 dark:focus:ring-orange-500"
          />
          <button
            type="submit"
            disabled={addLoading || !newKeyword.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                       bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed
                       text-white transition-colors"
          >
            {addLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add
          </button>
        </form>
        <p className="mt-2 text-xs text-gray-400">
          Keywords are stored lowercase. Play Store search is performed with <code>hl=en&gl=us</code>.
        </p>
      </div>

    </div>
  );
};

export default StoreRankingsPage;
